// src/pages/Profile/components/ProfileView.jsx
import React, { useState } from 'react';
import html2pdf from 'html2pdf.js';
import CVPDFTemplate from './CVPDFTemplate';

const ProfileView = ({ profile, onEdit, completion, isComplete }) => {
    const [generating, setGenerating] = useState(false);
    
    const experienceLabels = {
        'NO_EXPERIENCE': 'No Experience',
        'ZERO_TO_ONE_YEAR': '0 - 1 Year',
        'ONE_TO_TWO_YEARS': '1 - 2 Years'
    };

    const handleDownloadCV = async () => {
        if (!profile) return;

        setGenerating(true);

        try {
            // Create a temporary container
            const tempDiv = document.createElement('div');
            tempDiv.style.position = 'absolute';
            tempDiv.style.left = '-9999px';
            tempDiv.style.top = '0';
            tempDiv.style.width = '794px';
            tempDiv.style.background = 'white';
            tempDiv.style.padding = '20px';
            tempDiv.style.zIndex = '9999';
            document.body.appendChild(tempDiv);

            // Use ReactDOM to render the CV template
            const ReactDOM = await import('react-dom/client');
            const root = ReactDOM.createRoot(tempDiv);
            root.render(<CVPDFTemplate profile={profile} />);

            // Wait for render
            await new Promise(resolve => setTimeout(resolve, 500));

            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `CV_${profile.firstName || 'User'}_${profile.lastName || 'Profile'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { 
                    scale: 2, 
                    useCORS: true,
                    logging: false,
                    letterRendering: true,
                },
                jsPDF: { 
                    unit: 'in', 
                    format: 'a4', 
                    orientation: 'portrait' 
                }
            };

            // Find the CV content inside the temp div
            const element = tempDiv.querySelector('#cv-pdf-content');
            if (element) {
                await html2pdf().set(opt).from(element).save();
            } else {
                alert('Could not generate CV. Please try again.');
            }

            // Clean up
            root.unmount();
            document.body.removeChild(tempDiv);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <>
            <div className="space-y-6 px-4 sm:px-0">
                {/* Header Section - Mobile Responsive */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                    <div className="text-center sm:text-left">
                        <h2 className="text-xl sm:text-2xl font-bold text-gradpath-dark">
                            {profile.firstName} {profile.lastName}
                        </h2>
                        <p className="text-gray-500 text-sm sm:text-base">{profile.email}</p>
                        {isComplete && (
                            <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                <i className="fas fa-check-circle mr-1"></i> Profile Complete
                            </span>
                        )}
                    </div>
                    
                    {/* Buttons - Each on their own row on mobile */}
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <button
                            onClick={handleDownloadCV}
                            disabled={generating}
                            className={`w-full sm:w-auto px-4 py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm sm:text-base ${
                                generating
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                        >
                            {generating ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-pdf"></i>
                                    Download as CV
                                </>
                            )}
                        </button>
                        <button
                            onClick={onEdit}
                            className="w-full sm:w-auto bg-gradpath-green text-white px-4 py-2 rounded-lg hover:bg-gradpath-dark transition flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                            <i className="fas fa-edit"></i> Edit Profile
                        </button>
                    </div>
                </div>

                {/* ===== REST OF THE PROFILE VIEW - UNCHANGED ===== */}
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div className="space-y-3">
                        <h3 className="font-bold text-gray-700 border-b pb-2">Personal Information</h3>
                        <div>
                            <span className="text-sm text-gray-500">Phone</span>
                            <p className="font-medium break-words">{profile.phoneNumber || 'Not provided'}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Location</span>
                            <p className="font-medium break-words">{profile.city}, {profile.country}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Current Job Title</span>
                            <p className="font-medium break-words">{profile.currentJobTitle || 'Not specified'}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Experience Level</span>
                            <p className="font-medium break-words">{experienceLabels[profile.experienceLevel] || 'Not specified'}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Date of Birth</span>
                            <p className="font-medium break-words">{profile.dateOfBirth || 'Not provided'}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Gender</span>
                            <p className="font-medium break-words">{profile.gender || 'Not specified'}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Citizenship</span>
                            <p className="font-medium break-words">{profile.citizenship || 'Not specified'}</p>
                        </div>
                        {profile.idNumber && (
                            <div>
                                <span className="text-sm text-gray-500">ID Number</span>
                                <p className="font-medium break-words">{profile.idNumber}</p>
                            </div>
                        )}
                    </div>

                    {/* Professional Links */}
                    <div className="space-y-3">
                        <h3 className="font-bold text-gray-700 border-b pb-2">Professional Links</h3>
                        
                        {profile.linkedin ? (
                            <div>
                                <span className="text-sm text-gray-500">
                                    <i className="fab fa-linkedin text-blue-600 mr-1"></i> LinkedIn
                                </span>
                                <p className="font-medium break-words">
                                    <a 
                                        href={profile.linkedin} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-gradpath-green hover:underline break-all"
                                    >
                                        {profile.linkedin}
                                    </a>
                                </p>
                            </div>
                        ) : (
                            <p className="text-gray-400 text-sm">No LinkedIn link added</p>
                        )}
                        
                        {profile.portfolio ? (
                            <div>
                                <span className="text-sm text-gray-500">
                                    <i className="fas fa-folder-open text-gradpath-green mr-1"></i> Portfolio / Website
                                </span>
                                <p className="font-medium break-words">
                                    <a 
                                        href={profile.portfolio} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-gradpath-green hover:underline break-all"
                                    >
                                        {profile.portfolio}
                                    </a>
                                </p>
                            </div>
                        ) : (
                            <p className="text-gray-400 text-sm">No portfolio link added</p>
                        )}

                        <div>
                            <span className="text-sm text-gray-500">Willing to Relocate</span>
                            <p className="font-medium">{profile.willingToRelocate ? '✅ Yes' : '❌ No'}</p>
                        </div>
                        <div>
                            <span className="text-sm text-gray-500">Desired Salary</span>
                            <p className="font-medium break-words">{profile.desiredSalary || 'Not specified'}</p>
                        </div>
                    </div>
                </div>

                {/* Job Preferences */}
                <div className="mt-6">
                    <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Job Preferences</h3>
                    <div>
                        <span className="text-sm text-gray-500">Looking For</span>
                        <p className="font-medium break-words">{profile.lookingFor || 'Not specified'}</p>
                    </div>
                    <div className="mt-2">
                        <span className="text-sm text-gray-500">Preferred Location</span>
                        <p className="font-medium break-words">{profile.preferredJobLocation || 'Not specified'}</p>
                    </div>
                </div>

                {/* Skills & Languages */}
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                    <div>
                        <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Skills</h3>
                        <div className="flex flex-wrap gap-2">
                            {profile.skills?.length > 0 ? (
                                profile.skills.map((skill, index) => (
                                    <span key={index} className="bg-green-50 text-gradpath-green px-3 py-1 rounded-full text-sm">
                                        {skill}
                                    </span>
                                ))
                            ) : (
                                <p className="text-gray-400 text-sm">No skills added</p>
                            )}
                        </div>
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Languages</h3>
                        <div className="flex flex-wrap gap-2">
                            {profile.languages?.length > 0 ? (
                                profile.languages.map((lang, index) => (
                                    <span key={index} className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm">
                                        {lang}
                                    </span>
                                ))
                            ) : (
                                <p className="text-gray-400 text-sm">No languages added</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Professional Summary */}
                {profile.professionalSummary && (
                    <div className="mt-6">
                        <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Professional Summary</h3>
                        <p className="text-gray-600 leading-relaxed break-words">{profile.professionalSummary}</p>
                    </div>
                )}

                {/* Education */}
                {profile.education?.length > 0 && (
                    <div className="mt-6">
                        <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Education</h3>
                        <div className="space-y-4">
                            {profile.education.map((edu, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-4">
                                    <p className="font-bold break-words">{edu.qualification}</p>
                                    <p className="text-gray-600 break-words">{edu.institution}</p>
                                    {(edu.startDate || edu.endDate) && (
                                        <p className="text-sm text-gray-500">
                                            {edu.startDate || 'Start date not specified'} 
                                            {edu.isCurrent ? ' - Present' : edu.endDate ? ` - ${edu.endDate}` : ''}
                                            {edu.isCurrent && (
                                                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                    Current
                                                </span>
                                            )}
                                        </p>
                                    )}
                                    {edu.keyHighlights && (
                                        <p className="text-sm text-gray-600 mt-2 border-t border-gray-200 pt-2">
                                            <span className="font-medium text-gray-700">Highlights:</span> {edu.keyHighlights}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Work Experience */}
                {profile.workExperience?.length > 0 && (
                    <div className="mt-6">
                        <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Work Experience</h3>
                        <div className="space-y-4">
                            {profile.workExperience.map((exp, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-4">
                                    <p className="font-bold break-words">{exp.title} at {exp.company}</p>
                                    <p className="text-sm text-gray-500">
                                        {exp.startDate || 'Start date not specified'} 
                                        {exp.isCurrent ? ' - Present' : exp.endDate ? ` - ${exp.endDate}` : ''}
                                        {exp.isCurrent && (
                                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                Current
                                            </span>
                                        )}
                                    </p>
                                    {exp.description && <p className="text-sm text-gray-600 mt-2 break-words">{exp.description}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Certifications & Awards */}
                {profile.certifications?.length > 0 && (
                    <div className="mt-6">
                        <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">
                            <i className="fas fa-certificate text-gradpath-green mr-2"></i>
                            Certifications & Awards
                        </h3>
                        <div className="space-y-3">
                            {profile.certifications.map((cert, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-3">
                                    <p className="font-bold break-words">{cert.name}</p>
                                    {cert.issuingOrganization && (
                                        <p className="text-sm text-gray-600 break-words">{cert.issuingOrganization}</p>
                                    )}
                                    {cert.year && (
                                        <p className="text-sm text-gray-500">{cert.year}</p>
                                    )}
                                    {cert.description && (
                                        <p className="text-sm text-gray-600 mt-1 break-words">{cert.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* References */}
                {profile.references?.length > 0 && (
                    <div className="mt-6">
                        <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">
                            <i className="fas fa-address-book text-gradpath-green mr-2"></i>
                            References
                        </h3>
                        <div className="space-y-3">
                            {profile.references.map((ref, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-3">
                                    <p className="font-bold break-words">{ref.name}</p>
                                    {ref.title && <p className="text-sm text-gray-600 break-words">{ref.title}</p>}
                                    {ref.company && <p className="text-sm text-gray-600 break-words">{ref.company}</p>}
                                    {(ref.email || ref.phone) && (
                                        <div className="mt-1 text-sm text-gray-500">
                                            {ref.email && <span>📧 {ref.email}</span>}
                                            {ref.phone && <span className="ml-3">📱 {ref.phone}</span>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ProfileView;